// @flow
import * as React from 'react';
import styled from 'styled-components';
import { sprintf } from 'sprintf-js';
import { removeArrayIndex } from 'utils/functional/array';
import Box from 'components/Box';
import Icon from 'components/Icon';
import Input from 'components/Input';
import SelectInput from 'components/SelectInput';
import Button, { LinkButton } from 'components/Button';
import { InputField, SelectField, FormContext } from 'components/Form';
import { getQaId, Label, Description } from './Field';
import { colors, transition } from '../../theme';

const { useContext } = React;

const DisabledRow = styled(Box)`
  && {
    input,
    select {
      opacity: 1;
      background: ${colors.platinum};
      box-shadow: none;
      border: 0;
      color: ${colors.greyishBrown};
    }
    svg {
      opacity: 0.33;
    }
  }
`;

const RemoveButton = styled(Button)`
  width: 100%;
  border-radius: 50%;
  color: ${colors.battleshipGrey};
  transition: all ${transition};
  &:focus,
  &:hover {
    color: ${colors.scarlet};
  }
`;

const RequestedAttachmentsField = ({
  name,
  label,
  description
}: {
  name: string,
  label?: string,
  description?: string
}) => {
  const { getValue, updateValueWith, fieldQaIdPrefix } =
    useContext(FormContext) || {};

  const value = getValue(name);
  const rows: Array<mixed> = value instanceof Array ? value : [];

  const qaId = fieldQaIdPrefix
    ? `${fieldQaIdPrefix}-${getQaId(name)}`
    : getQaId(name);

  const addRow = () => {
    if (rows.length < 5) {
      updateValueWith(name, v => [...v, { isRequired: null, label: null }]);
    }
  };

  const removeRow = index =>
    updateValueWith(name, v => removeArrayIndex(v, index));

  return (
    <Box
      data-qa-id={qaId}
      data-qa-group-count={rows.length}
      id={`${name}__root`}
      mb={40}
    >
      {label && <Label>{label}</Label>}

      {description && <Description>{description}</Description>}

      <DisabledRow display="flex" flexWrap="wrap" mx="-6px">
        <Box p="6px" width={[1, 1 / 2]} flex="0 0 auto">
          <Input
            aria-label={getText('Resume')}
            value={getText('Resume')}
            disabled
          />
        </Box>
        <Box p="6px" width={[1, 1 / 2]} flex="0 0 auto">
          <SelectInput
            value="true"
            aria-label={getText('Document status')}
            options={[{ label: 'Required', value: 'true' }]}
            disabled
          />
        </Box>
      </DisabledRow>

      {rows.map((row, index) => {
        const namePrefix = `${name}.${index}.`;
        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            data-qa-id={`${qaId}-${index}`}
            position="relative"
            display="flex"
            flexWrap="wrap"
            mx="-6px"
            mt="3px"
            mb={-30}
          >
            <Box p="6px" width={[1, 1 / 2]} flex="0 0 auto">
              <InputField
                aria-label={
                  /* TRANSLATORS: label for requested attachments label field */ sprintf(
                    getText('Label for %(label)s document #%(number)s'),
                    {
                      label,
                      number: index + 1
                    }
                  )
                }
                name={`${namePrefix}label`}
                placeholder={getText('Enter a name for this file')}
              />
            </Box>
            <Box p="6px" width={[1, 1 / 2]} flex="0 0 auto">
              <SelectField
                aria-label={
                  /* TRANSLATORS: label for requested attachments status field */ sprintf(
                    getText('Status for %(label)s document #%(number)s'),
                    {
                      label,
                      number: index + 1
                    }
                  )
                }
                name={`${namePrefix}isRequired`}
                options={[
                  { label: 'Document status', value: '' },
                  { label: 'Required', value: 'true' },
                  { label: 'Optional', value: 'false' }
                ]}
              />
            </Box>
            <Box position="absolute" top={12} left="100%" ml="-2px" width={32}>
              <RemoveButton
                data-qa-id="remove-section"
                aria-label={getText('Remove document row')}
                onClick={() => removeRow(index)}
              >
                <Box
                  height={32}
                  width={1}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon name="trashcan" width={14} fill="currentColor" />
                </Box>
              </RemoveButton>
            </Box>
          </Box>
        );
      })}

      {rows.length < 5 && (
        <Box mt={12} position="relative">
          <LinkButton onClick={addRow} data-qa-id={`${qaId}-add`}>
            + {getText('Request Another Document')}
          </LinkButton>
        </Box>
      )}
    </Box>
  );
};

export default RequestedAttachmentsField;
